import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { Badge, EmptyState, IconButton, Page, PageHeader, TableSkeleton } from "../../../components/ui";
import { getCategoryDetail, writeCategorySummary } from "../../../services/categoryStats";
import { formatDuration } from "../video-performance/VideoPerformance";
import VideoPerformanceDetail, { audienceChart, dropOffChart } from "../video-performance/VideoPerformanceDetail";
import { fmt, FormatFilter, MoodBar, PeriodFilter, usePeriod, Versus } from "./shared";
import "../payments-ledger/payments.css";
import "../video-performance/video-performance.css";
import "./category-stats.css";

const ago = (date, t) => {
  if (!date) return "—";
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 864e5);
  if (days <= 0) return t("Today");
  if (days === 1) return t("Yesterday");
  return t("{{count}} days ago", { count: days });
};
const rankText = (rank, of, t) => (of > 1 ? t(rank === 1 ? "first of {{of}} rubrics" : "{{rank}} of {{of}} rubrics", { rank, of }) : null);

/** Strengths, weaknesses or ideas, one column. */
const InsightList = ({ icon, title, items, tone, empty }) => (
  <section className="ui-card cat-insights" data-tone={tone}>
    <h2>
      <i className={icon} aria-hidden="true"></i> {title}
    </h2>
    {items.length ? (
      <ul>
        {items.map((item) => (
          <li key={item.text}>{item.text}</li>
        ))}
      </ul>
    ) : (
      <p className="ui-hint">{empty}</p>
    )}
  </section>
);

/** A short list of videos with one figure, each opening its performance. */
const VideoList = ({ title, hint, items, figure, onOpen }) => {
  const { t } = useTranslation();
  return (
    <section className="ui-card cat-videos">
      <h2>{title}</h2>
      {hint && <p className="ui-hint">{hint}</p>}
      {items.length ? (
        <ol>
          {items.map((v) => (
            <li key={v.id}>
              <button type="button" onClick={() => onOpen(v.id)}>
                {v.thumbnail ? <img src={v.thumbnail} alt="" loading="lazy" /> : <span className="cat-thumb" aria-hidden="true"><i className="ri-film-line"></i></span>}
                <span className="cat-video-title" title={v.title}>{v.title}</span>
                <span className="cat-video-figure">{figure(v)}</span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="ui-hint">{t("Not enough plays yet")}</p>
      )}
    </section>
  );
};

/**
 * One rubric over a period: figures against the platform (and its rank among the rubrics),
 * strengths, weaknesses and ideas, an optional summary written by the AI, what the comments say,
 * audience per day, where viewers stop, and the videos that carry or hold back the rubric.
 */
const CategoryDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const period = usePeriod(location, history);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiState, setAiState] = useState({ busy: false, error: null });
  const [opened, setOpened] = useState(null);
  const paramsKey = JSON.stringify(period.params);
  // t change à chaque rendu tant que les traductions chargent : hors des dépendances du chargement
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(
    (fresh) => {
      const params = JSON.parse(paramsKey);
      if (!params) return;
      setData(null);
      setError(null);
      setAiState({ busy: false, error: null });
      getCategoryDetail(id, { ...params, ...(fresh === true ? { fresh: 1 } : {}) })
        .then((result) => {
          setData(result);
          setAi(result.ai);
        })
        .catch((e) => setError(e?.response?.data?.message || tRef.current("The list could not be loaded.")));
    },
    [id, paramsKey]
  );
  useEffect(() => load(), [load]);

  const writeSummary = (regenerate) => {
    setAiState({ busy: true, error: null });
    writeCategorySummary(id, JSON.parse(paramsKey), regenerate)
      .then((result) => {
        setAi(result);
        setAiState({ busy: false, error: null });
      })
      .catch((e) => setAiState({ busy: false, error: e?.response?.data?.message || t("The summary could not be written, try again later.") }));
  };

  const c = data?.category;
  const p = data?.platform;
  const audience = data?.daily?.some((d) => d.views || d.started) ? audienceChart(data.daily, t, "cat-audience") : null;
  const drop = data?.dropOff?.plays ? dropOffChart(data.dropOff, t) : null;
  const insights = data?.insights;

  return (
    <Page>
      <PageHeader
        title={c ? c.name : t("Stats by rubric")}
        subtitle={
          c
            ? [
                t("{{count}} videos", { count: c.videos, formatted: fmt(c.videos) }),
                c.shorts ? t("{{count}} shorts", { count: c.shorts }) : null,
                c.series ? t("{{count}} series ({{episodes}} episodes)", { count: c.series, episodes: c.episodes }) : null,
                t("last publication: {{when}}", { when: ago(c.lastPublishedAt, t).toLowerCase() }),
              ]
                .filter(Boolean)
                .join(" · ")
            : t("How each rubric performs: audience, how far people watch, reactions, what the comments say, and where to improve.")
        }
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={() => load(true)} />}
      />

      <div className="perf-filters">
        <Link to={`/dashboard/category-stats${period.search}`} className="ui-btn ui-btn-secondary cat-back">
          <i className="ri-arrow-left-s-line" aria-hidden="true"></i> {t("All rubrics")}
        </Link>
        {data?.others?.length > 1 && (
          <select className="ui-input perf-sort" aria-label={t("Rubric")} value={id} onChange={(e) => history.push(`/dashboard/category-stats/${e.target.value}${period.search}`)}>
            {data.others.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
        <FormatFilter state={period} />
        <PeriodFilter state={period} />
        {data?.computedAt && (
          <span className="ui-hint perf-computed">{t("Figures of {{time}}", { time: new Date(data.computedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}</span>
        )}
      </div>

      {error ? (
        <section className="ui-card">
          <EmptyState icon="ri-error-warning-line">
            {error}{" "}
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => load(true)}>
              {t("Retry")}
            </button>
          </EmptyState>
        </section>
      ) : !data ? (
        <section className="ui-card">
          <TableSkeleton rows={8} />
        </section>
      ) : (
        <>
          <div className="ledger-stats perf-stats">
            <div className="ui-card ledger-stat">
              <strong>{fmt(c.views)}</strong>
              <span>{t("Views")}</span>
              {c.trend !== null && <small className="perf-delta" data-tone={c.trend > 0 ? "up" : c.trend < 0 ? "down" : "flat"}>{c.trend > 0 ? "+" : ""}{c.trend} % {t("vs previous period")}</small>}
              {rankText(data.ranks.views, data.ranks.of, t) && <small className="ui-hint">{rankText(data.ranks.views, data.ranks.of, t)}</small>}
            </div>
            <div className="ui-card ledger-stat">
              <strong>{fmt(c.viewsPerVideo)}</strong>
              <span>{t("Views per video")}</span>
              <Versus value={c.viewsPerVideo} reference={p.medianViewsPerVideo} label={t("vs the median rubric")} />
            </div>
            <div className="ui-card ledger-stat">
              <strong>{c.completion !== null ? `${c.completion} %` : "—"}</strong>
              <span>{t("Plays watched to the end")}</span>
              <Versus value={c.completion} reference={p.completion} unit="pts" />
              {c.completion !== null && rankText(data.ranks.completion, data.ranks.of, t) && <small className="ui-hint">{rankText(data.ranks.completion, data.ranks.of, t)}</small>}
            </div>
            <div className="ui-card ledger-stat">
              <strong>{c.earlyDrop !== null ? `${c.earlyDrop} %` : "—"}</strong>
              <span>{t("Left in the first 10 %")}</span>
              {c.earlyDrop !== null && p.earlyDrop !== null && (
                <small className="perf-delta" data-tone={c.earlyDrop <= p.earlyDrop ? "up" : "down"}>
                  {c.earlyDrop - p.earlyDrop > 0 ? "+" : ""}
                  {c.earlyDrop - p.earlyDrop} pts {t("vs platform")}
                </small>
              )}
            </div>
            <div className="ui-card ledger-stat">
              <strong>{c.reactionRate ?? "—"}</strong>
              <span>{t("Reactions per 100 views")}</span>
              <Versus value={c.reactionRate} reference={p.reactionRate} />
              {c.reactionRate !== null && rankText(data.ranks.reactionRate, data.ranks.of, t) && <small className="ui-hint">{rankText(data.ranks.reactionRate, data.ranks.of, t)}</small>}
            </div>
            <div className="ui-card ledger-stat">
              <strong>{fmt(c.comments + c.replies)}</strong>
              <span>{t("Comments and replies")}</span>
              <small className="ui-hint">
                {fmt(c.likes)} {t("likes")} · {fmt(c.shares)} {t("shares")} · {fmt(c.thanks)} Merci
              </small>
            </div>
            <div className="ui-card ledger-stat">
              <strong>{c.sentiment.score ?? "—"}</strong>
              <span>{t("Sentiment index")}</span>
              <Versus value={c.sentiment.score} reference={p.sentiment?.score} unit="pts" />
            </div>
            <div className="ui-card ledger-stat">
              <strong>{c.watchSeconds ? formatDuration(c.watchSeconds) : "—"}</strong>
              <span>{t("Watch time")}</span>
              {c.avgDuration ? <small className="ui-hint">{t("average length {{value}}", { value: formatDuration(c.avgDuration) })}</small> : null}
            </div>
          </div>

          <div className="cat-insight-grid">
            <InsightList icon="ri-thumb-up-line" title={t("Strengths")} items={insights.strengths} tone="good" empty={t("Nothing stands out above the platform over this period.")} />
            <InsightList icon="ri-error-warning-line" title={t("Weak points")} items={insights.weaknesses} tone="bad" empty={t("No weak point against the platform over this period.")} />
            <InsightList icon="ri-lightbulb-line" title={t("Ideas to improve")} items={insights.ideas} tone="idea" empty={t("Keep going: nothing to correct as a priority.")} />
          </div>

          <section className="ui-card cat-ai">
            <div className="cat-ai-head">
              <h2>
                <i className="ri-quill-pen-line" aria-hidden="true"></i> {t("Editorial summary (AI)")}
              </h2>
              {ai ? (
                <button type="button" className="ui-btn ui-btn-secondary" onClick={() => writeSummary(true)} disabled={aiState.busy}>
                  {aiState.busy ? t("Writing…") : t("Write it again")}
                </button>
              ) : (
                <button type="button" className="ui-btn" onClick={() => writeSummary(false)} disabled={aiState.busy}>
                  {aiState.busy ? t("Writing…") : t("Write the summary")}
                </button>
              )}
            </div>
            {aiState.error && <p className="cat-ai-error" role="alert">{aiState.error}</p>}
            {ai ? (
              <div className="cat-ai-body">
                {ai.summary && <p className="cat-ai-summary">{ai.summary}</p>}
                {ai.mood && <p><strong>{t("What the comments say")} :</strong> {ai.mood}</p>}
                <div className="cat-ai-columns">
                  {[
                    [t("Strengths"), ai.strengths],
                    [t("Weak points"), ai.weaknesses],
                    [t("Ideas to improve"), ai.ideas],
                  ].map(([title, list]) =>
                    list?.length ? (
                      <div key={title}>
                        <h3>{title}</h3>
                        <ul>
                          {list.map((x) => (
                            <li key={x}>{x}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null
                  )}
                </div>
                <p className="ui-hint">{t("Written {{date}} from the figures and {{count}} comments (text only, without names). To be checked: the AI can be wrong.", { date: new Date(ai.at).toLocaleString(), count: ai.comments })}</p>
              </div>
            ) : (
              <p className="ui-hint">{t("The AI reads the figures of the rubric and a sample of its comments (text only, without names or accounts) and writes a short summary with ideas. On request only.")}</p>
            )}
          </section>

          <section className="ui-card cat-sentiment">
            <h2>{t("What the comments say")}</h2>
            <MoodBar mood={data.sentiment} />
            {data.sentiment.themes.length > 0 && (
              <div className="cat-themes">
                <span className="ui-label">{t("Recurring words")}</span>
                <div>
                  {data.sentiment.themes.map((th) => (
                    <span key={th.word} className="cat-theme" data-tone={th.tone} title={t("{{count}} comments", { count: th.count, formatted: fmt(th.count) })}>
                      {th.word} <small>{th.count}</small>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="cat-examples">
              {[
                ["positive", t("Positive comments"), "ri-emotion-happy-line"],
                ["negative", t("Critical comments"), "ri-emotion-unhappy-line"],
              ].map(([key, title, icon]) => (
                <div key={key} data-tone={key}>
                  <h3>
                    <i className={icon} aria-hidden="true"></i> {title}
                  </h3>
                  {data.sentiment.examples[key].length ? (
                    data.sentiment.examples[key].map((e, i) => (
                      <blockquote key={e.id || i}>
                        « {e.comment} »
                        <footer>
                          {e.video ? e.video : ""}
                          {e.likes ? ` · ${t("{{count}} likes", { count: e.likes })}` : ""}
                        </footer>
                      </blockquote>
                    ))
                  ) : (
                    <p className="ui-hint">{t("None over the period")}</p>
                  )}
                </div>
              ))}
            </div>
            {data.sentiment.byVideo.length > 0 && (
              <div className="ui-table-wrap">
                <table className="ui-table cat-mood-table">
                  <thead>
                    <tr>
                      <th>{t("Most commented videos")}</th>
                      <th>{t("Comments")}</th>
                      <th>{t("Sentiment")}</th>
                      <th>{t("Index")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sentiment.byVideo.map((v) => (
                      <tr key={v.id} className="ledger-row" onClick={() => setOpened(v.id)}>
                        <td>{v.title}</td>
                        <td>{fmt(v.total)}</td>
                        <td className="cat-mood-cell">
                          <MoodBar mood={{ ...v, neutral: v.total - v.positive - v.negative }} compact />
                        </td>
                        <td>
                          <Badge color={v.score >= 30 ? "green" : v.score <= 0 ? "red" : "slate"}>{v.score}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {(audience || drop) && (
            <div className="perf-charts">
              {audience && (
                <section className="ui-card perf-top">
                  <h2>{t("Audience per day")}</h2>
                  <Chart type="line" height={260} options={audience.options} series={audience.series} />
                  {data.period === "all" && <p className="ui-hint">{t("« All time »: the charts cover the last 90 days.")}</p>}
                </section>
              )}
              {drop && (
                <section className="ui-card perf-top">
                  <h2>{t("Where viewers stop")}</h2>
                  <Chart type="bar" height={260} options={drop.options} series={drop.series} />
                  <p className="ui-hint">{t("{{count}} plays in the app and on the site over the period.", { count: data.dropOff.plays })}</p>
                </section>
              )}
            </div>
          )}

          <div className="cat-video-grid">
            <VideoList title={t("Most viewed")} items={data.videos.top} onOpen={setOpened} figure={(v) => `${fmt(v.views)} ${t("views")}`} />
            <VideoList title={t("Watched to the end the most")} items={data.videos.bestKept} onOpen={setOpened} figure={(v) => `${v.completion} %`} />
            <VideoList title={t("To rework")} hint={t("Left early the most (10 plays at least)")} items={data.videos.toWork} onOpen={setOpened} figure={(v) => `${v.completion} %`} />
            <VideoList title={t("Most reactions")} hint={t("Per 100 views (50 views at least)")} items={data.videos.mostReactive} onOpen={setOpened} figure={(v) => `${v.reactionRate} / 100`} />
          </div>

          <p className="ui-hint perf-hint">
            {t("Strengths and weak points compare the rubric with the whole platform (gap of 15 % at least, with enough plays and views). Sentiment: comments read automatically; an indication over many comments, not a judgement on one.")}
          </p>
        </>
      )}
      {opened && (
        <VideoPerformanceDetail id={opened} period={period.params || { period: "30d" }} averages={p ? { avgCompletion: p.completion, avgWatched: p.watched } : null} onClose={() => setOpened(null)} />
      )}
    </Page>
  );
};

export default CategoryDetail;
